import React, { useState, useEffect, useCallback } from 'react';

function ThemeSwitcherBall({ theme, setTheme }) {
  const ballSize = 60; // 球的直径
  const margin = 20;   // 距离视口边缘的最小距离

  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [moved, setMoved] = useState(false);

  // === 初始化位置 ===
  useEffect(() => {
    setPosition({
      x: window.innerWidth - ballSize - margin,
      y: window.innerHeight - ballSize - margin,
    });
  }, []);

  // === 保证窗口缩放时不溢出 ===
  useEffect(() => {
    const handleResize = () => {
      setPosition(prev => ({
        x: Math.min(prev.x, window.innerWidth - ballSize - margin),
        y: Math.min(prev.y, window.innerHeight - ballSize - margin),
      }));
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // === PC 拖拽 ===
  const handleMouseDown = useCallback((e) => {
    setDragging(true);
    setMoved(false);
    setOffset({
      x: e.clientX - position.x,
      y: e.clientY - position.y,
    });
  }, [position]);

  const handleMouseMove = useCallback((e) => {
    if (dragging) {
      let newX = e.clientX - offset.x;
      let newY = e.clientY - offset.y;

      newX = Math.max(margin, Math.min(newX, window.innerWidth - ballSize - margin));
      newY = Math.max(margin, Math.min(newY, window.innerHeight - ballSize - margin));

      setPosition({ x: newX, y: newY });
      setMoved(true);
    }
  }, [dragging, offset]);

  const handleMouseUp = useCallback(() => {
    setDragging(false);
  }, []);

  // === 手机拖拽 ===
  const handleTouchStart = useCallback((e) => {
    setDragging(true);
    setMoved(false);
    const touch = e.touches[0];
    setOffset({
      x: touch.clientX - position.x,
      y: touch.clientY - position.y,
    });
  }, [position]);

  const handleTouchMove = useCallback((e) => {
    if (dragging) {
      e.preventDefault(); // 关键：阻止页面滚动！
      const touch = e.touches[0];
      let newX = touch.clientX - offset.x;
      let newY = touch.clientY - offset.y;

      newX = Math.max(margin, Math.min(newX, window.innerWidth - ballSize - margin));
      newY = Math.max(margin, Math.min(newY, window.innerHeight - ballSize - margin));

      setPosition({ x: newX, y: newY });
      setMoved(true);
    }
  }, [dragging, offset]);

  const handleTouchEnd = useCallback(() => {
    setDragging(false);
  }, []);

  // === 全局监听 ===
  useEffect(() => {
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    // 必须设置 passive: false 才能生效 preventDefault
    window.addEventListener('touchmove', handleTouchMove, { passive: false });
    window.addEventListener('touchend', handleTouchEnd);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
      window.removeEventListener('touchmove', handleTouchMove, { passive: false });
      window.removeEventListener('touchend', handleTouchEnd);
    };
  }, [handleMouseMove, handleMouseUp, handleTouchMove, handleTouchEnd]);

  // === 双击切换主题（PC） / 单击切换（Mobile） ===
  const toggleTheme = () => {
    if (moved) return;
    setTheme(theme === 'light' ? 'dark' : 'light');
  };

  return (
    <div
      onMouseDown={handleMouseDown}
      onDoubleClick={toggleTheme}
      onTouchStart={handleTouchStart}
      onTouchEnd={() => {
        handleTouchEnd();
        if (!moved) toggleTheme();
      }}
      style={{
        position: 'fixed',
        top: position.y,
        left: position.x,
        width: `${ballSize}px`,
        height: `${ballSize}px`,
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
        cursor: dragging ? 'grabbing' : 'grab',
        zIndex: 9999,
        userSelect: 'none',
        boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
        transition: dragging ? 'none' : 'box-shadow 0.3s ease',
      }}
    >
      <img
        src={theme === 'light' ? '/assets/img/sun.png' : '/assets/img/moon.png'}
        alt={theme === 'light' ? 'Sun Icon' : 'Moon Icon'}
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          borderRadius: '50%',
          pointerEvents: 'none',
        }}
      />
    </div>
  );
}

export default ThemeSwitcherBall;
